import {CircularProgress, Grid2, Grid2 as Grid, Typography} from "@mui/material";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {useModelState} from "@/lib/modelstate";
import * as React from "react";
import {Constants} from "@/api/constants";
import {useTranslation} from "react-i18next";
import {CommonI18N, CompanyI18N} from "@/translations";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import {countryOptions} from "@/datacaches";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import {AddressDTO, LegalForm} from "@/api/data-contracts";
import {addressApi} from "@/boxhub-api";
import {DialogEx} from "@/lib/components/DialogEx";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {LegalFormForCountryDialog} from "@/pages/company-page/LegalFormForCountryDialog";
import {useAddressEditSchema} from "@/common/address/addressEditSchema";
import {useGuidedLegalFormCountryChange} from "@/common/address/useGuidedLegalFormCountryChange";

export type AddressEditDialogDialogProps = {
    open: boolean;
    close: () => void;
    onSuccess?: () => void;
    address?: AddressDTO;
    companyId?: number;
    currentLegalForm?: LegalForm;
};

export const AddressEditDialog = ({
                                      open,
                                      close,
                                      onSuccess,
                                      address,
                                      companyId,
                                      currentLegalForm
                                  }: AddressEditDialogDialogProps) => {
    const { t } = useTranslation();
    const countrySelectOptions = countryOptions.map((option) => ({
        label: option && t(CommonI18N.enum.country[option]),
        value: option
    }));
    const schema = useAddressEditSchema();
    const {model, errors, onChange, save, validate} = useModelState<AddressDTO>({
        initialState: {
            ...address,
            deliveryInstruction: address?.deliveryInstruction ?? {}
        } as AddressDTO,
        schema,
        updater: addressApi.updateAddress1,
        id: address?.id
    });

    // Shared guided flow (see useGuidedLegalFormCountryChange): an incompatible company-address
    // country change opens the follow-up dialog; confirming persists country and legal form
    // atomically via the combined endpoint.
    const {
        legalFormDialogOpen,
        closeLegalFormDialog,
        handleSave,
        handleLegalFormConfirm,
        saving,
        legalFormsByCountry,
        legalFormsByCountryStatus
    } = useGuidedLegalFormCountryChange({
        addressId: address?.id,
        isCompanyAddress: model?.companyAddress,
        originalCountry: address?.country,
        newCountry: model?.country,
        currentLegalForm,
        validate,
        save,
        confirmUpdate: (addressId, legalForm) =>
                addressApi.updateAddressWithLegalForm(addressId, {address: model, legalForm}),
        onSaved: () => {
            close();
            onSuccess?.();
        }
    });

    const buttons = (
        <>
            <Grid2>
                <ButtonEx label={t(CommonI18N.action.cancel)} color="secondary" disabled={saving} onClick={close}/>
            </Grid2>
            <Grid2>
                <ButtonEx
                        label={(saving && <CircularProgress size={24}/>) || t(CompanyI18N.form.action.save)}
                        color="primary"
                        disabled={saving}
                        onClick={handleSave}
                />
            </Grid2>
        </>
    );

    return (
            <>
        <DialogEx
                title={t(CompanyI18N.form.page.addressEditTitle)}
            open={open}
            onClose={close}
            maxWidth="md"
            fullWidth
            data-cy={companyId ? `address-edit-dialog-${companyId}` : undefined}
            buttons={buttons}
        >
            <Grid container spacing={2} padding={2} width="100%">
                <Grid container spacing={2} size={{ xs: 12 }}>
                    <Grid size={{ sm: 8, xs: 12 }}>
                        <StringInputField
                            name="street"
                            value={model?.street}
                            label={t(CompanyI18N.form.field.street)}
                            error={errors?.street}
                            maxChars={Constants.STREET_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ sm: 4, xs: 12 }}>
                        <StringInputField
                            name="houseNumber"
                            value={model?.houseNumber}
                            label={t(CompanyI18N.form.field.houseNumber)}
                            error={errors?.houseNumber}
                            maxChars={Constants.HOUSE_NUMBER_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                    <StringInputField
                        name="zip"
                        value={model?.zip}
                        label={t(CompanyI18N.form.field.postalCode)}
                        error={errors?.zip}
                        maxChars={Constants.ZIP_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                    <StringInputField
                        name="city"
                        value={model?.city}
                        label={t(CompanyI18N.form.field.city)}
                        error={errors?.city}
                        maxChars={Constants.CITY_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                    <SelectInputField
                        name="country"
                        label={t(CompanyI18N.form.field.country)}
                        value={model?.country}
                        error={errors?.country}
                        options={countrySelectOptions}
                        displayEmpty={true}
                        onChange={onChange}
                    />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                    <CheckboxInputField
                        name="companyAddress"
                        label={t(CompanyI18N.form.field.headquarter)}
                        onChange={onChange}
                        disabled={true}
                        value={model?.companyAddress}
                        error={errors?.companyAddress}
                    />
                </Grid>
                <Grid size={{ xs: 12 }} mt={2}>
                    <Typography variant="h6">{t(CompanyI18N.form.field.deliveryInstructionsTitle)}</Typography>
                </Grid>
                <Grid container spacing={2} size={{ sm: 6, xs: 12 }} direction="column">
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            name="deliveryInstruction.mondayInstruction"
                            value={model?.deliveryInstruction?.mondayInstruction}
                            label={t(CompanyI18N.form.field.monday)}
                            error={errors?.deliveryInstruction?.mondayInstruction}
                            maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            name="deliveryInstruction.tuesdayInstruction"
                            value={model?.deliveryInstruction?.tuesdayInstruction}
                            label={t(CompanyI18N.form.field.tuesday)}
                            error={errors?.deliveryInstruction?.tuesdayInstruction}
                            maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            name="deliveryInstruction.wednesdayInstruction"
                            value={model?.deliveryInstruction?.wednesdayInstruction}
                            label={t(CompanyI18N.form.field.wednesday)}
                            error={errors?.deliveryInstruction?.wednesdayInstruction}
                            maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            name="deliveryInstruction.thursdayInstruction"
                            value={model?.deliveryInstruction?.thursdayInstruction}
                            label={t(CompanyI18N.form.field.thursday)}
                            error={errors?.deliveryInstruction?.thursdayInstruction}
                            maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            name="deliveryInstruction.fridayInstruction"
                            value={model?.deliveryInstruction?.fridayInstruction}
                            label={t(CompanyI18N.form.field.friday)}
                            error={errors?.deliveryInstruction?.fridayInstruction}
                            maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            name="deliveryInstruction.saturdayInstruction"
                            value={model?.deliveryInstruction?.saturdayInstruction}
                            label={t(CompanyI18N.form.field.saturday)}
                            error={errors?.deliveryInstruction?.saturdayInstruction}
                            maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            name="deliveryInstruction.sundayInstruction"
                            value={model?.deliveryInstruction?.sundayInstruction}
                            label={t(CompanyI18N.form.field.sunday)}
                            error={errors?.deliveryInstruction?.sundayInstruction}
                            maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2} size={{ sm: 6, xs: 12 }} direction="column">
                    <Grid size={{ xs: 12 }}>
                        <CheckboxInputField
                            name="deliveryInstruction.timeSlotBookingRequired"
                            label={t(CompanyI18N.form.field.isTimeSlotBooking)}
                            onChange={onChange}
                            value={model?.deliveryInstruction?.timeSlotBookingRequired}
                            error={errors?.deliveryInstruction?.timeSlotBookingRequired}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            multiline={true}
                            name="deliveryInstruction.arrivalNotification"
                            value={model?.deliveryInstruction?.arrivalNotification}
                            label={t(CompanyI18N.form.field.preAdviceNote)}
                            error={errors?.deliveryInstruction?.arrivalNotification}
                            maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </DialogEx>
                <LegalFormForCountryDialog
                        open={legalFormDialogOpen}
                        newCountry={model?.country}
                        address={model}
                        legalFormsByCountry={legalFormsByCountry}
                        legalFormsByCountryStatus={legalFormsByCountryStatus}
                        saving={saving}
                        onConfirm={handleLegalFormConfirm}
                        onCancel={closeLegalFormDialog}
                />
            </>
    );
};
